import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { fetch } from '@/lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

type User = {
  id: number;
  username: string;
  fullName: string;
  email: string;
  profilePicture?: string;
};

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, fullName: string, email: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  setError: (error: string | null) => void;
  isLoading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasLoggedOut, setHasLoggedOut] = useState(false);

  const { data: fetchedUser } = useQuery({
    queryKey: ['/api/user'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/user', {
          credentials: 'include',
          headers: {
            'content-type': 'application/json',
          },
        });
        if (!response.ok) {
          if (response.status === 401) {
            // Not authenticated, but not an error for our purposes
            return null;
          }
          throw new Error('Failed to fetch user data');
        }
        const text = await response.text();
        try { return JSON.parse(text); } catch { return null; }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching user:', err);
        }
        return null;
      }
    },
    retry: false,
    refetchOnWindowFocus: false
  });
  
  // Update user state when fetchedUser changes
  useEffect(() => {
    if (fetchedUser) {
      setUser(fetchedUser);
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  }, [fetchedUser]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ username, password }: { username: string; password: string }) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('loginMutation mutationFn called with:', username);
      }
      try {
        const response = await fetch('/api/login', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username, password }),
        });

        const data = await response.json().catch(() => ({ 
          message: response.status === 401 
            ? 'Invalid username or password' 
            : 'An error occurred' 
        }));
        
        if (!response.ok) {
          setError(data.message || 'Login failed');
          return null;
        }

        setError(null);
        return data;
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Login error:', err);
        }
        setError('Network error occurred. Please try again.');
        return null;
      }
    },
    onSuccess: (data) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('loginMutation success:', data);
      }
      if (data ) {
        if (data.user) {
          setUser(data.user);  // Update AuthContext state
          setIsAuthenticated(true);
        }
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      }
    }
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async ({ username, password, fullName, email }: { username: string; password: string; fullName: string; email: string }) => {
      try {
        const response = await fetch('/api/register', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username, password, fullName, email }),
        });

        let data;
        try {
          data = await response.json();
        } catch {
          // Response may not be JSON, fallback to text or a default error message
          const text = await response.text();
          const errorMsg = text || 'Registration failed: Invalid server response';
          setError(errorMsg);
          throw new Error('Registration failed: Invalid server response');
        }

        if (!response.ok) {
          const errorMsg = data.message || 'Registration failed';
          setError(errorMsg || 'Registration failed');
          throw new Error(errorMsg);
        }

        setError(null);
        return data;
      } catch (err: any) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Registration error:', err);
        }
        setError(err.message || 'Network error occurred. Please try again.');
        throw err;
      }
    },
    onSuccess: (data) => {
      if (data) {
        if (data.user) {
          setUser(data.user);
        }
        queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      }
    }
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        const response = await fetch('/api/logout', {
          method: 'POST',
        });

        if (!response.ok) {
          setError('Logout failed');
          return false;
        }

        setError(null);
        return true;
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Logout error:', err);
        }
        setError('Network error during logout');
        return false;
      }
    },
    onSuccess: (success) => {
      if (success) {
        queryClient.invalidateQueries({ queryKey: ['/api/user'] });
        queryClient.clear(); // Clear all queries when logging out
        setUser(null); // Clear user state
        setHasLoggedOut(true);
      }
    }
  });

  const login = async (username: string, password: string) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('login function called from context', username, password);
    }
    await loginMutation.mutateAsync({ username, password });
  };

  const register = async (username: string, password: string, fullName: string, email: string) => {
    setError(''); // Clear previous error before starting
    try {
      const response = await registerMutation.mutateAsync({ username, password, fullName, email });
      if (process.env.NODE_ENV !== 'production') {
        console.log('Register mutation response:', response);
      }
      // Defensive check for null or invalid response
      if (!response) {
        const errorMsg = 'Registration failed: No response received';
        setError(errorMsg);
        throw new Error(errorMsg);
      }
      if (response.error) {
        setError(response.error);
        throw new Error(response.error);
      }
      return response;
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      throw err; // rethrow so caller knows registration failed
    }
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        error,
        setError,
        hasLoggedOut,
        resetLogoutFlag: () => setHasLoggedOut(false),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
